import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Briefcase, Users, TrendingUp, Headphones, Globe, 
  ArrowRight, CheckCircle, Send, Mail, MapPin
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

const positions = [
  {
    title: "Affiliate Manager",
    type: "Full-time / Remote",
    description: "Manage and grow relationships with our affiliate publishers. You'll be responsible for onboarding new affiliates, optimizing their performance, and ensuring quality traffic.",
    requirements: [
      "2+ years experience in affiliate marketing",
      "Strong understanding of Pay-Per-Call industry",
      "Excellent communication skills",
      "Experience with tracking platforms (Ringba, Retreaver)",
      "Self-motivated and results-driven"
    ],
    icon: Users
  },
  {
    title: "Publisher Manager",
    type: "Full-time / Remote",
    description: "Support and develop our publisher network. Help publishers maximize their earnings while ensuring compliance with our quality standards.",
    requirements: [
      "1+ years in publisher relations or account management",
      "Knowledge of digital marketing channels",
      "Strong analytical skills",
      "Experience with call tracking software",
      "Ability to work independently"
    ],
    icon: Briefcase
  },
  {
    title: "Media Buyer",
    type: "Full-time / Remote",
    description: "Run and optimize paid advertising campaigns across Google, Facebook, and other platforms to generate high-quality phone calls for our buyers.",
    requirements: [
      "3+ years experience in paid media buying",
      "Proven track record with Google Ads and Facebook Ads",
      "Experience generating phone calls (not just clicks)",
      "Strong ROI optimization skills",
      "Daily budget management experience ($1k+)"
    ],
    icon: TrendingUp
  },
  {
    title: "Business Development Manager",
    type: "Full-time / Remote",
    description: "Identify and close new buyer partnerships. You'll be responsible for expanding our network of call buyers across various home service verticals.",
    requirements: [
      "3+ years B2B sales experience",
      "Understanding of lead generation industry",
      "Strong negotiation skills",
      "Excellent presentation abilities",
      "Track record of meeting/exceeding targets"
    ],
    icon: Globe
  },
  {
    title: "Customer Support Representative",
    type: "Full-time / Part-time / Remote",
    description: "Provide excellent support to our publishers and buyers. Handle inquiries, resolve issues, and ensure partner satisfaction.",
    requirements: [
      "1+ years customer support experience",
      "Strong written and verbal communication",
      "Problem-solving mindset",
      "Familiarity with CRM systems",
      "Available for flexible hours"
    ],
    icon: Headphones
  }
];

const benefits = [
  "100% Remote Work",
  "Competitive Salary + Bonuses",
  "Flexible Working Hours",
  "Growth Opportunities",
  "Training & Development",
  "Performance Incentives"
];

const CareersPage = () => {
  const [selectedPosition, setSelectedPosition] = useState(null);

  return (
    <div className="min-h-screen bg-slate-50 font-['Inter']" data-testid="careers-page">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0f172a] overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 relative">
          <motion.div 
            initial="hidden" 
            animate="visible" 
            variants={stagger}
            className="text-center"
          >
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-green-500/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-green-500/30">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              <span className="text-green-400 text-sm font-medium">We're Hiring!</span>
            </motion.div>
            
            <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Join Our <span className="gradient-text">Growing Team</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-gray-300 text-lg max-w-2xl mx-auto">
              Be part of a dynamic team shaping the future of pay-per-call marketing. 
              Work remotely with talented professionals from around the world.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-block text-blue-600 font-semibold text-sm uppercase tracking-wider mb-2">
              Why Join Us
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Benefits & <span className="gradient-text">Perks</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-4 text-center"
              >
                <CheckCircle className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="font-medium text-gray-900 text-sm">{benefit}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-16 bg-gray-50" data-testid="positions-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-block text-blue-600 font-semibold text-sm uppercase tracking-wider mb-2">
              Open Positions
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Current <span className="gradient-text">Opportunities</span>
            </h2>
          </div>
          
          <div className="space-y-6">
            {positions.map((position, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden"
                data-testid={`position-${position.title.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div 
                  className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setSelectedPosition(selectedPosition === index ? null : index)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl flex items-center justify-center">
                        <position.icon className="w-7 h-7 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{position.title}</h3>
                        <p className="text-gray-500">{position.type}</p>
                      </div>
                    </div>
                    <ArrowRight className={`w-6 h-6 text-gray-400 transition-transform ${selectedPosition === index ? 'rotate-90' : ''}`} />
                  </div>
                </div>
                
                {selectedPosition === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    className="px-6 pb-6 border-t border-gray-100"
                  >
                    <div className="pt-6">
                      <p className="text-gray-600 mb-6">{position.description}</p>
                      
                      <h4 className="font-bold text-gray-900 mb-3">Requirements:</h4>
                      <ul className="space-y-2 mb-6">
                        {position.requirements.map((req, i) => (
                          <li key={i} className="flex items-start gap-2 text-gray-600">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span>{req}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <Link
                        to={`/job-apply?position=${position.title.toLowerCase().replace(/\s/g, '_')}`}
                        className="gradient-btn text-white font-semibold px-6 py-3 rounded-full inline-flex items-center gap-2"
                      >
                        <Send className="w-5 h-5" />
                        Apply Now
                      </Link>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 relative overflow-hidden" data-testid="careers-cta">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
        
        <div className="max-w-4xl mx-auto px-4 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Don't See Your Role?
            </h2>
            <p className="text-white/90 text-lg mb-10 max-w-2xl mx-auto">
              We're always looking for talented individuals. Send us your resume and tell us how you can contribute to our team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/job-apply"
                className="bg-white text-blue-600 hover:bg-gray-100 font-bold px-10 py-4 rounded-full inline-flex items-center justify-center gap-2 transition-all"
              >
                <Send className="w-5 h-5" />
                Submit Your Resume
              </Link>
              <Link 
                to="/contact" 
                className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-bold px-10 py-4 rounded-full inline-flex items-center justify-center gap-2 border border-white/30 transition-all"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CareersPage;
